// src/utils/imageUtils.js

/**
 * Baixa uma imagem com fundo branco (importante para desenhos para colorir)
 * @param {string} imageUrl - URL da imagem para download
 * @param {string} fileName - Nome do arquivo para o download
 */
export const downloadWithWhiteBackground = async (imageUrl, fileName = 'imagem.png') => {
  try {
    // Buscar a imagem da URL
    const response = await fetch(imageUrl);

    if (!response.ok) {
      throw new Error(`Erro HTTP: ${response.status}`);
    }

    const blob = await response.blob();

    // Criar uma imagem para renderizar no canvas
    const img = new Image();

    // Quando a imagem carregar, desenhar em um canvas com fundo branco
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Definir dimensões do canvas
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      // Preencher com fundo branco
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Desenhar a imagem sobre o fundo branco
      ctx.drawImage(img, 0, 0);

      // Converter para blob e fazer download
      canvas.toBlob((blob) => {
        // Criar URL para o blob
        const url = URL.createObjectURL(blob);

        // Criar elemento de link para download
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();

        // Limpar
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 100);
      }, 'image/png');
    };

    // Configurar fonte da imagem
    img.src = URL.createObjectURL(blob);

    return true;
  } catch (error) {
    console.error('Erro ao baixar imagem:', error);
    throw error;
  }
};
/**
 * Extrai as dimensões de uma imagem a partir de uma URL ou Blob
 * @param {string|Blob} source - URL da imagem ou objeto Blob
 * @returns {Promise<{width: number, height: number}>} Dimensões da imagem
 */
export const getImageDimensions = (source) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    };

    img.onerror = () => {
      reject(new Error('Erro ao carregar a imagem para obter dimensões'));
    };

    if (typeof source === 'string') {
      img.src = source;
    } else if (source instanceof Blob) {
      img.src = URL.createObjectURL(source);
    } else {
      reject(new Error('Fonte de imagem inválida'));
    }
  });
};

/**
 * Calcula dimensões apropriadas para manter a proporção da imagem dentro de um container
 * @param {number} imgWidth - Largura original da imagem
 * @param {number} imgHeight - Altura original da imagem
 * @param {number} containerWidth - Largura do container
 * @param {number} containerHeight - Altura do container
 * @returns {{width: number, height: number}} Dimensões proporcionais
 */
export const calculateContainDimensions = (imgWidth, imgHeight, containerWidth, containerHeight) => {
  const imgRatio = imgWidth / imgHeight;
  const containerRatio = containerWidth / containerHeight;

  let width, height;

  if (imgRatio > containerRatio) {
    // Imagem mais larga que o container (proporcionalmente)
    width = containerWidth;
    height = width / imgRatio;
  } else {
    // Imagem mais alta que o container (proporcionalmente)
    height = containerHeight;
    width = height * imgRatio;
  }

  return { width, height };
};

/**
 * Cria um preview otimizado de uma imagem local
 * @param {File} file - Arquivo de imagem
 * @param {number} maxDimension - Dimensão máxima para o preview (largura ou altura)
 * @returns {Promise<string>} URL do preview
 */
export const createOptimizedPreview = (file, maxDimension = 400) => {
  return new Promise((resolve, reject) => {
    // Criar um FileReader para ler o arquivo
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();

      img.onload = () => {
        // Obter dimensões originais
        const { naturalWidth, naturalHeight } = img;

        // Calcular novas dimensões
        let newWidth, newHeight;

        if (naturalWidth > naturalHeight) {
          // Imagem horizontal
          if (naturalWidth > maxDimension) {
            newWidth = maxDimension;
            newHeight = (naturalHeight * maxDimension) / naturalWidth;
          } else {
            newWidth = naturalWidth;
            newHeight = naturalHeight;
          }
        } else {
          // Imagem vertical
          if (naturalHeight > maxDimension) {
            newHeight = maxDimension;
            newWidth = (naturalWidth * maxDimension) / naturalHeight;
          } else {
            newWidth = naturalWidth;
            newHeight = naturalHeight;
          }
        }

        // Criar um canvas para redimensionar
        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;

        // Desenhar a imagem redimensionada
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        // Converter para URL de dados
        const preview = canvas.toDataURL('image/jpeg', 0.85);
        resolve(preview);
      };

      img.onerror = () => {
        reject(new Error('Erro ao carregar imagem para otimização'));
      };

      img.src = event.target.result;
    };

    reader.onerror = () => {
      reject(new Error('Erro ao ler o arquivo de imagem'));
    };

    reader.readAsDataURL(file);
  });
};